<script setup>
import Logo from '@/components/icons/Logo.vue';
import { useLayout } from '@/layout/common/composables/layout';
import adminMenuItems from '@/layout/admin/menu';
import Sidebar from '@/layout/main/sidebar';
import { Link, router } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading';
import { getCurrentInstance } from 'vue';
import logoUrl from '@/assets/login-logo.png';

const { calculatedMenuMode } = useLayout();
const { showLoading, hideLoading } = useLoading();
const { proxy } = getCurrentInstance();
const $t = proxy.$t;

// Function to check if a menu item is active
const isMenuItemActive = (item) => {
    if (item.route) {
        try {
            return route().current(item.route);
        } catch (error) {
            console.warn(`Route '${item.route}' not found for active check`, error);
            return false;
        }
    }
    return false;
};

// Function to get the href for menu items
const getMenuItemHref = (item) => {
    if (item.route) {
        try {
            return route(item.route, item.params || {});
        } catch (error) {
            console.warn(`Route '${item.route}' not found in route list`, error);
            return '#';
        }
    }
    return item.url || item.to || '#';
};

// Function to handle navigation with loading
const handleNavigation = (item) => {
    if (item.route) {
        showLoading($t('Loading...'));
        router.visit(route(item.route, item.params || {}), {
            onFinish: () => hideLoading(),
            onError: () => hideLoading()
        });
    }
};
</script>

<template>
    <Sidebar>
        <!-- Header -->
        <Sidebar.Header>
            <Link :href="route('dashboard')" class="flex items-center gap-2">
                <div class="flex items-center justify-center">
                    <img :src="logoUrl" class="h-14 w-auto" alt="Logo" />
                </div>
            </Link>
        </Sidebar.Header>
        <!-- Content -->
        <Sidebar.Content v-if="calculatedMenuMode !== 'slim' && calculatedMenuMode !== 'compact' && calculatedMenuMode !== 'horizontal'">
            <Sidebar.Group v-for="menuItem in adminMenuItems" :key="menuItem.label">
                <Sidebar.GroupLabel>{{ $t(menuItem.label) }}</Sidebar.GroupLabel>
                <Sidebar.GroupContent>
                    <Sidebar.Menu>
                        <Sidebar.MenuItem v-for="item in menuItem.items" :key="item.label" :collapsible="!!item.items">
                            <Sidebar.MenuButton 
                                :href="getMenuItemHref(item)" 
                                :target="item.target" 
                                :is-active="isMenuItemActive(item)" 
                                :is-first="true"
                                @click="() => handleNavigation(item)"
                            >
                                <i :class="item.icon" class="!leading-none !text-sm" />
                                <span>{{ $t(item.label) }}</span>
                            </Sidebar.MenuButton>
                            <Sidebar.Menu v-if="item.items">
                                <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label">
                                    <Sidebar.MenuButton 
                                        :href="getMenuItemHref(subItem)" 
                                        :target="subItem.target" 
                                        :is-active="isMenuItemActive(subItem)"
                                        @click="() => handleNavigation(subItem)"
                                    >
                                        <i :class="subItem.icon" class="!leading-none !text-sm" />
                                        <span>{{ subItem.label }}</span>
                                    </Sidebar.MenuButton>
                                </Sidebar.MenuItem>
                            </Sidebar.Menu>
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.GroupContent>
            </Sidebar.Group>
        </Sidebar.Content>
        <Sidebar.Content v-else>
            <Sidebar.Group>
                <Sidebar.GroupContent>
                    <Sidebar.Menu>
                        <Sidebar.MenuItem v-for="item in adminMenuItems" :key="item.label">
                            <Sidebar.Popover :placement="calculatedMenuMode === 'horizontal' ? 'bottom' : 'right'">
                                <template #trigger>
                                    <Sidebar.MenuButton 
                                        :href="getMenuItemHref(item)" 
                                        :target="item.target" 
                                        :is-active="isMenuItemActive(item)" 
                                        :tooltip-value="calculatedMenuMode === 'compact' ? item.label : ''"
                                        @click="() => handleNavigation(item)"
                                    >
                                        <i :class="item.icon" class="!leading-none !text-sm" />
                                        <span>{{ item.label }}</span>
                                    </Sidebar.MenuButton>
                                </template>
                                <template #content v-if="item.items">
                                    <Sidebar.Menu mode="static">
                                        <Sidebar.MenuItem v-for="subItem in item.items" :key="subItem.label" :collapsible="!!subItem.items">
                                            <Sidebar.MenuButton 
                                                mode="static" 
                                                :href="getMenuItemHref(subItem)" 
                                                :target="subItem.target" 
                                                :is-active="isMenuItemActive(subItem)"
                                                @click="() => handleNavigation(subItem)"
                                            >
                                                <i :class="subItem.icon" class="!leading-none !text-sm" />
                                                <span>{{ subItem.label }}</span>
                                            </Sidebar.MenuButton>
                                            <Sidebar.Menu mode="static" v-if="subItem.items">
                                                <Sidebar.MenuItem v-for="subSubItem in subItem.items" :key="subSubItem.label">
                                                    <Sidebar.MenuButton 
                                                        mode="static" 
                                                        :href="getMenuItemHref(subSubItem)" 
                                                        :target="subSubItem.target" 
                                                        :is-active="isMenuItemActive(subSubItem)"
                                                        @click="() => handleNavigation(subSubItem)"
                                                    >
                                                        <i :class="subSubItem.icon" class="!leading-none !text-sm" />
                                                        <span>{{ subSubItem.label }}</span>
                                                    </Sidebar.MenuButton>
                                                </Sidebar.MenuItem>
                                            </Sidebar.Menu>
                                        </Sidebar.MenuItem>
                                    </Sidebar.Menu>
                                </template>
                            </Sidebar.Popover>
                        </Sidebar.MenuItem>
                    </Sidebar.Menu>
                </Sidebar.GroupContent>
            </Sidebar.Group>
        </Sidebar.Content>
    </Sidebar>
</template>